import { MAIL_BUILD_VERSION } from "@/lib/cache/mailBuildVersion";
import {
  enableMailWebPush,
  isMailWebPushSupported,
  readMailWebPushPref,
} from "@/lib/mail/mailWebPush";

const MAIL_WEB_PUSH_SW_KEY = "mail_web_push_sw_build";

/** Resubscribe si la préférence est on. Après un nouveau SW, forcer un endpoint frais. */
export async function syncMailWebPushOnBoot(): Promise<void> {
  if (!readMailWebPushPref()) return;
  if (!isMailWebPushSupported()) return;
  if (Notification.permission !== "granted") return;

  let force = false;
  try {
    force = localStorage.getItem(MAIL_WEB_PUSH_SW_KEY) !== MAIL_BUILD_VERSION;
  } catch {
    force = true;
  }

  if (force && "serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    } catch {
      /* ignore */
    }
  }

  await enableMailWebPush();
  try {
    localStorage.setItem(MAIL_WEB_PUSH_SW_KEY, MAIL_BUILD_VERSION);
  } catch {
    /* ignore */
  }
}

/**
 * Mail au premier plan : pas de toast OS (son in-app déjà là).
 * iOS ne signale souvent pas client.focused — heartbeat + pagehide.
 */
export function installMailPushFocusGuard(): () => void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return () => undefined;
  }

  const post = (focused: boolean) => {
    try {
      navigator.serviceWorker.controller?.postMessage({
        type: "MAIL_CLIENT_FOCUS",
        focused,
      });
    } catch {
      /* ignore */
    }
  };

  const dismissVisible = () => {
    post(true);
    void navigator.serviceWorker.ready
      .then((reg) => reg.getNotifications())
      .then((list) => {
        for (const n of list) n.close();
      })
      .catch(() => undefined);
  };

  const sync = () => {
    if (document.visibilityState === "visible") dismissVisible();
    else post(false);
  };

  const onHidden = () => post(false);

  sync();
  const id = window.setInterval(() => {
    if (document.visibilityState === "visible") post(true);
  }, 2000);
  document.addEventListener("visibilitychange", sync);
  window.addEventListener("focus", sync);
  window.addEventListener("pagehide", onHidden);
  window.addEventListener("blur", onHidden);
  document.addEventListener("freeze", onHidden);
  const onHub = (event: MessageEvent) => {
    const t = (event.data as { type?: string } | null)?.type;
    if (t === "AURELIA_MAIL_FRAME_HIDDEN" || t === "AURELIA_SUITE_LOGOUT") {
      post(false);
    }
  };
  window.addEventListener("message", onHub);
  return () => {
    window.clearInterval(id);
    document.removeEventListener("visibilitychange", sync);
    window.removeEventListener("focus", sync);
    window.removeEventListener("pagehide", onHidden);
    window.removeEventListener("blur", onHidden);
    document.removeEventListener("freeze", onHidden);
    window.removeEventListener("message", onHub);
    post(false);
  };
}
